import React, { useState, useRef, useEffect } from 'react';

const GooeyLink = ({ children, href, className = '', target = null, isDarkMode = false, onClick, style = {}, textColor }) => {
  const [isHovered, setIsHovered] = useState(false);
  const itemRef = useRef(null);
  const textRef = useRef(null);
  const iconRef = useRef(null);
  const backgroundRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const hoverTimeoutRef = useRef(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  
  // Detect touch device on mount
  useEffect(() => {
    const touchDevice = 'ontouchstart' in window || 
                       navigator.maxTouchPoints > 0 || 
                       navigator.msMaxTouchPoints > 0;
    setIsTouchDevice(touchDevice);
  }, []);

  // Handle click events for both mouse and touch
  const handleClick = (e) => {
    // Only use custom handling for mailto links
    if (href && href.startsWith('mailto:')) {
      e.preventDefault(); // Only prevent default for mailto links
      
      // On mobile devices, try to prevent any interference
      if (isTouchDevice) {
        e.stopPropagation();
        
        // For mobile devices, try the mailto first
        try {
          window.location.href = href;
        } catch (error) {
          // Fallback: copy email to clipboard and show message
          const email = href.replace('mailto:', '').split('?')[0];
          navigator.clipboard.writeText(email).then(() => {
            alert(`Email copied to clipboard: ${email}`);
          }).catch(() => {
            alert(`Please email: ${email}`);
          });
        }
        return;
      } else {
        // For desktop, just use the href normally
        window.location.href = href;
        return;
      }
    }
    
    // For all other links, let them work normally
    // Don't prevent default, don't interfere
    if (onClick) {
      onClick(e);
    }
  };

  // Touch event handlers for mobile devices
  const handleTouchStart = (e) => {
    if (isTouchDevice) {
      setIsHovered(true);
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (isTouchDevice) {
      // Set a timeout to remove hover state after touch
      hoverTimeoutRef.current = setTimeout(() => {
        setIsHovered(false);
        resetBorderRadius();
        hoverTimeoutRef.current = null;
      }, 150);
    }
  };

  // Helper function to reset border radius
  const resetBorderRadius = () => {
    if (itemRef.current) {
      itemRef.current.style.borderRadius = '100px';
    }
    
    if (backgroundRef.current) {
      backgroundRef.current.style.borderRadius = '100px';
    }
    
    // Reset CSS variables
    if (itemRef.current) {
      itemRef.current.style.removeProperty('--tl-radius');
      itemRef.current.style.removeProperty('--tr-radius');
      itemRef.current.style.removeProperty('--br-radius');
      itemRef.current.style.removeProperty('--bl-radius');
    }
  };

  // Handle mouse movement to update the position for the gooey effect
  const handleMouseMove = (e) => {
    // Skip mouse events on touch devices to prevent conflicts
    if (isTouchDevice || !itemRef.current) return;
    
    const rect = itemRef.current.getBoundingClientRect();
    // Convert to percentage values for more consistent effects across different sized items
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setMousePosition({ x, y });
    
    // Calculate dynamic border radius based on mouse position
    if (isHovered && itemRef.current) {
      // Calculate distance from each corner to create a bulging effect
      const width = rect.width;
      const height = rect.height;
      
      // Calculate distance from each corner
      const distanceFromTopLeft = Math.sqrt(
        Math.pow(e.clientX - rect.left, 2) + 
        Math.pow(e.clientY - rect.top, 2)
      );
      
      const distanceFromTopRight = Math.sqrt(
        Math.pow(e.clientX - (rect.left + width), 2) + 
        Math.pow(e.clientY - rect.top, 2)
      );
      
      const distanceFromBottomRight = Math.sqrt(
        Math.pow(e.clientX - (rect.left + width), 2) + 
        Math.pow(e.clientY - (rect.top + height), 2)
      );
      
      const distanceFromBottomLeft = Math.sqrt(
        Math.pow(e.clientX - rect.left, 2) + 
        Math.pow(e.clientY - (rect.top + height), 2)
      );
      
      // Normalize distances to create a bulging effect
      // The closer the mouse is to a corner, the smaller the radius
      const maxDistance = Math.sqrt(width * width + height * height);
      const baseRadius = 100; // Match the 100px border radius
      const variationRange = 70; // Increased variation range for more dramatic effect
      
      const tl = baseRadius - (variationRange * (1 - distanceFromTopLeft / maxDistance));
      const tr = baseRadius - (variationRange * (1 - distanceFromTopRight / maxDistance));
      const br = baseRadius - (variationRange * (1 - distanceFromBottomRight / maxDistance));
      const bl = baseRadius - (variationRange * (1 - distanceFromBottomLeft / maxDistance));
      
      // Apply the dynamic border radius to the main element
      itemRef.current.style.borderRadius = `${tl}px ${tr}px ${br}px ${bl}px`;
      
      // Also update the CSS variable to control the ::before pseudo-element
      itemRef.current.style.setProperty('--tl-radius', `${tl}px`);
      itemRef.current.style.setProperty('--tr-radius', `${tr}px`);
      itemRef.current.style.setProperty('--br-radius', `${br}px`);
      itemRef.current.style.setProperty('--bl-radius', `${bl}px`);
      
      // Apply the same border radius to the background element
      if (backgroundRef.current) {
        backgroundRef.current.style.borderRadius = `${tl}px ${tr}px ${br}px ${bl}px`;
      }
    }
  };
  
  const handleMouseEnter = () => {
    // Skip mouse events on touch devices
    if (isTouchDevice) return;
    
    // Clear any existing timeout to prevent state conflicts
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovered(true);
  };
  
  const handleMouseLeave = () => {
    // Skip mouse events on touch devices
    if (isTouchDevice) return;
    
    // Use a moderate timeout for balanced response when leaving
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
      resetBorderRadius();
      hoverTimeoutRef.current = null;
    }, 100); // Moderate timeout for balanced response
  };
  
  // Add a document-level mouse move handler to detect when mouse is far from the element
  useEffect(() => {
    // Skip this effect on touch devices
    if (isTouchDevice || !isHovered) return;
    
    const handleDocumentMouseMove = (e) => {
      if (!itemRef.current) return;
      
      const itemRect = itemRef.current.getBoundingClientRect();
      
      // Calculate distance from the element with a buffer zone
      const bufferZone = 20; // Increased buffer zone
      const isOutsideBufferZone = 
        e.clientX < itemRect.left - bufferZone ||
        e.clientX > itemRect.right + bufferZone ||
        e.clientY < itemRect.top - bufferZone ||
        e.clientY > itemRect.bottom + bufferZone;
      
      // If mouse is outside the buffer zone, turn off hover state
      if (isOutsideBufferZone) {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
        
        hoverTimeoutRef.current = setTimeout(() => {
          setIsHovered(false);
          resetBorderRadius();
          hoverTimeoutRef.current = null;
        }, 100); // Moderate timeout for balanced response
      }
    };
    
    document.addEventListener('mousemove', handleDocumentMouseMove);
    
    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseMove);
    };
  }, [isHovered, isTouchDevice]);
  
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Custom SVG arrow outward icon
  const LinkIcon = ({ color }) => (
    <div
      ref={iconRef}
      style={{
        display: 'inline-flex',
        marginLeft: '0px',
        position: 'relative',
        top: '1px',
        paddingLeft: '2px',
        opacity: 0,
        transform: 'scale(0.8)',
        transition: 'opacity 0.5s ease, transform 0.5s ease', // Longer transition
        ...(isHovered ? { opacity: 1, transform: 'scale(1)' } : {})
      }}
    >
      <svg 
        width="18" 
        height="18" 
        viewBox="0 0 24 24" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        style={{ 
          display: 'block',
          width: '20px',
          height: '20px'
        }}
      >
        {/* Arrow outward icon - with appropriate color */}
        <path 
          d="M6 19L19 6M19 6H8M19 6V17" 
          stroke={color}
          strokeWidth="2.5" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );

  // SIMPLIFIED text color logic with !important
  let finalTextColor = '#000000'; // Default
  
  if (isHovered) {
    finalTextColor = '#000000'; // Always black on hover
  } else if (isDarkMode) {
    finalTextColor = '#FFFFFF'; // Always white in dark mode
  } else if (textColor) {
    finalTextColor = textColor; // Use provided color in light mode
  }

  return (
    <div
      ref={itemRef}
      className={`gooey-link ${isHovered ? 'hovered' : ''} ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={handleClick}
      style={{
        '--mouse-x': `${mousePosition.x}%`,
        '--mouse-y': `${mousePosition.y}%`,
        display: 'inline-block',
        position: 'relative',
        padding: '2px 0px',
        borderRadius: '100px',
        cursor: 'pointer',
        transform: isHovered ? 'translateY(-3px)' : 'translateY(0)',
        transition: 'transform 0.5s cubic-bezier(0.34, 0.56, 0.64, 1), border-radius 0.5s cubic-bezier(0.34, 0.56, 0.64, 1), opacity 0.5s ease', // Longer transitions
        ...style
      }}
    >
      {/* Background element with ref for hover detection */}
      <div 
        ref={backgroundRef}
        className="gooey-background"
        style={{
          position: 'absolute',
          top: -8,
          left: -8,
          right: -8,
          bottom: -8,
          borderRadius: '100px', // Match the main element's border radius
          transition: 'border-radius 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.5s ease', // Removed box-shadow transition
          opacity: isHovered ? 1 : 0,
          // Removed the green drop shadow
          // The CSS class will handle the background styling
        }}
      />
      
      <a 
        ref={textRef}
        href={href}
        target={href && href.startsWith('mailto:') ? '_self' : target} // Use _self for mailto links
        onClick={(e) => {
          // Only call our custom handler, don't always prevent default
          handleClick(e);
        }}
        style={{ 
          position: 'relative',
          zIndex: 5,
          color: `${finalTextColor} !important`,
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'color 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)', // Longer transition
          background: 'transparent' // Ensure no background behind the text
        }}
      >
        <span style={{ 
          color: `${finalTextColor} !important`,
        //   transition: 'color 0.5s ease', // Longer transition
          background: 'transparent' // Ensure no background behind the text
        }}>
          {children}
        </span>
        <LinkIcon color={finalTextColor} />
      </a>
      
      {/* SVG filter for gooey effect - separate from content */}
      <svg width="0" height="0" style={{ position: 'absolute', top: 0, left: 0 }}>
        <defs>
          <filter id="gooey-link">
            <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
            <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7" result="gooey" />
            <feComposite in="SourceGraphic" in2="gooey" operator="atop" />
          </filter>
        </defs>
      </svg>
    </div>
  );
};

export default GooeyLink; 